import {
  SlotRecipeConfig,
  SystemConfig,
  createSystem,
  defaultConfig,
  defineConditions,
  defineGlobalStyles,
  defineRecipe,
  defineTextStyles
} from "@chakra-ui/react"
import { getColorPaletteSemanticTokens } from "../../../../utils/color"

// NOTE: using defineRecipe is not required. You could inline the recipes into the theme.
// But if you want to reference variantes in the defaultVariants for example (without TS
// errors), you need to define them first using defineRecipe.

export const buttonProps = {
  base: {
    borderRadius: "{radii.button}",
    cursor: "pointer",
    fontFamily: "body",
    fontWeight: "semibold"
  },
  variants: {
    variant: {
      secondaryGray: {
        bg: "rgba(153, 153, 153, 0.7)",
        color: "white",
        backdropFilter: "blur(20px)",
        _hover: {
          bg: "rgba(153, 153, 153, 0.6)",
          color: "rgba(255, 255, 255, 0.9)"
        }
      },
      blurredLightGray: {
        bg: "rgba(255, 255, 255, 0.4)",
        color: "rgba(42,46,51,0.7)",
        backdropFilter: "blur(10px)",
        _hover: {
          bg: "rgba(255, 255, 255, 0.3)",
          color: "rgba(42,46,51,0.6)"
        }
      },
      "legacy.default": {
        bg: "rgba(41, 46, 53, 0.05)",
        color: "rgb(41, 46, 53)",
        borderColor: "rgba(41, 46, 53, 0.5)",
        fontWeight: "normal",
        _hover: {
          bg: "rgba(41, 46, 53, 0.1)",
          color: "rgba(41, 46, 53, 0.9)",
          borderColor: "rgba(41, 46, 53, 0.5)"
        }
      },
      "legacy.interactiveSecondary": {
        bg: "rgba(42,46,51,0.05)",
        color: "rgba(42,46,51,1)",
        backdropFilter: "blur(20px)",
        _hover: {
          bg: "rgba(42,46,51,0.1)",
          color: "rgba(42,46,51,0.9)"
        }
      },
      border: {
        backgroundColor: "transparent",
        borderRadius: "4px",
        border: "1px solid",
        borderColor: "BORDER_GRAY",
        px: 3,
        py: 2
      },
      // For use with Button component asChild wrapping a Link component,
      // when functionally you want a link but visually you want a mix of button and link.
      // One use case is when you need to disable a link which Link doesn't support.
      link: {
        p: 0,
        h: "auto",
        fontSize: "md",
        fontWeight: "normal",
        textDecoration: "underline",
        "--mix-textDecorationColor":
          "color-mix(in srgb, currentColor 20%, transparent)",
        textDecorationColor: "var(--mix-textDecorationColor, currentColor)",
        textUnderlineOffset: "3px"
      }
    },
    size: {
      sm: {
        fontSize: "sm" // decrease from the default of "xs"
      },
      md: {
        fontSize: "md" // increase from the default of "sm"
      },
      lg: {
        gap: 2, // reduce from the default of 3
        fontSize: "lg" // increase from the default of "md"
      }
    }
    // NOTE: the "auto" height breaks IconButton sizing (esp. when rounded="full").
    // If we need these size variation overrides, we need to figure out how to
    // do so without breaking IconButton.
    // size: {
    //   md: { fontSize: "sm", px: "32px", py: "12px", h: "auto" },
    //   lg: { fontSize: "18px", px: "32px", py: "13px", h: "auto" }
    // }
  }
  // Button should be sized as "md" by default anyhow.
  // defaultVariants: {
  //   size: "md"
  // }
}

const buttonRecipe = defineRecipe(buttonProps)

const containerRecipe = defineRecipe({
  base: {
    maxW: "1200px",
    px: ["sm", "sm", "md", "lg"]
  }
})

const drawerSlotRecipe: Partial<SlotRecipeConfig> = {
  base: {
    content: { textStyle: "md" }
  },
  variants: {
    variant: {
      aside: {
        content: {
          pointerEvents: "auto",
          borderRadius: "20px",
          bg: "whiteAlpha.800",
          backdropFilter: "blur(20px)",
          boxShadow: "none"
        },
        positioner: {
          pointerEvents: "none"
        },
        header: {
          borderTopRadius: "20px",
          px: 6,
          py: 3
        },
        body: {
          px: 6,
          py: 3,
          "&:not(:has(~ .chakra-drawer__footer))": {
            borderBottomRadius: "20px"
          }
        },
        footer: {
          borderBottomRadius: "20px",
          px: 6,
          py: 3
        }
      }
    }
  }
} as unknown as SlotRecipeConfig

const separatorRecipe = defineRecipe({
  base: { borderColor: "rgba(0, 0, 0, 0.1)" }
})

const headingRecipe = defineRecipe({
  base: {
    color: "blackAlpha.900",
    // Chakra v2 used a default font weight of 700 for headings.
    fontWeight: 700
  },
  variants: {
    size: {
      h8: { fontSize: "14px", lineHeight: "115%", fontWeight: 500 },
      h7: { fontSize: "17px", lineHeight: "115%", fontWeight: 500 },
      h6: { fontSize: "19px", lineHeight: "120%", fontWeight: 500 },
      h5: {
        fontSize: ["19px", "22px"],
        lineHeight: "110%",
        fontWeight: 500
      },
      h4: { fontSize: "24px", lineHeight: "120%", fontWeight: 500 },
      h3: {
        fontSize: ["32px", "36px"],
        lineHeight: "105%",
        fontWeight: 700
      },
      h2: {
        fontSize: ["42px", "48px"],
        lineHeight: "110%",
        letterSpacing: "-2px",
        fontWeight: 700
      },
      h1: {
        fontSize: ["46px", "52px", "56px"],
        lineHeight: "110%",
        letterSpacing: "-3px",
        fontWeight: 700
      },
      billboard: {
        fontSize: ["48px", "62px", "72px"],
        lineHeight: "90%",
        letterSpacing: "-4px",
        fontWeight: 700
      },
      jumbotron: {
        fontSize: ["56px", "72px", "90px"],
        lineHeight: "95%",
        letterSpacing: "-7px",
        fontWeight: 500
      }
    }
  }
})

const inputRecipe = defineRecipe({
  base: {
    // TODO: figure out how to apply styles to addon.
    // addon: {
    //   bg: "blue"
    // }
  },
  variants: {
    size: {
      md: { textStyle: "md" }
    },
    variant: {
      outline: {
        // TODO: figure out how to apply styles to field.
        // field: {
        //   border: "1px solid",
        //   borderColor: "BORDER_GRAY"
        // }
      },
      filled: {
        // TODO: figure out how to apply styles to field.
        // field: {
        //   borderRadius: "40px"
        // },
        // TODO: figure out how to apply styles to addon.
        // addon: {
        //   borderRadius: "40px"
        // }
      }
    }
  }
})

const textareaRecipe = defineRecipe({
  variants: {
    size: {
      md: { textStyle: "md" }
    }
  }
})

const popoverRecipe = defineRecipe({
  base: {
    // TODO: figure out how to apply styles to content.
    // content: {
    //   _focus: {
    //     boxShadow: "none",
    //     outline: "none" },
    //   borderRadius: "10px"
    // }
  }
})

const textRecipe = defineRecipe({
  base: {
    fontFamily: "body",
    fontWeight: 400,
    color: "blackAlpha.900",
    textAlign: "left"
  },
  variants: {
    size: {
      mono: {
        fontFamily: "Mono",
        fontSize: "18px",
        lineHeight: "140%",
        letterSpacing: "-1px"
      },
      micro: {
        fontSize: "12px",
        lineHeight: "120%",
        fontWeight: 500
      },
      xxs: { fontSize: "14px", lineHeight: "120%", fontWeight: 400 },
      xs: { fontSize: "16px", lineHeight: "120%", fontWeight: 400 },
      sm: { fontSize: "18px", lineHeight: "120%", fontWeight: 400 },
      md: { fontSize: "20px", lineHeight: "120%", fontWeight: 400 },
      lg: { fontSize: "28px", lineHeight: "112%", fontWeight: 400 }
    }
  }
})

const fieldSlotRecipe: Partial<SlotRecipeConfig> = {
  variants: {
    size: {
      lg: {
        root: { gap: "3" },
        label: { textStyle: "md" },
        helperText: { textStyle: "sm" },
        errorText: { textStyle: "sm" }
      }
    }
  }
} as unknown as SlotRecipeConfig

export const conditions = defineConditions({
  // For styling that depends on whether the device has hover capability.
  mediaHoverNone: "@media (hover: none)"
})

// Chakra v3 typography compatibility: v3 reduced default font sizes (e.g. sm = 0.875rem).
// To maintain Chakra v2 behavior, bump sm to md (1rem) so components using textStyle: "sm"
// render at the prior effective size.
const typographyCompatTextStyles = defineTextStyles({
  sm: {
    value: {
      // fontSize: "md",
      // lineHeight: "1.5rem"
    }
  },
  // Every string in an augmentation panel — field values, list labels, menu
  // rows, static copy (which adds 0.6 opacity). Figma: Inter 500 14/17.
  augmentationPanel: {
    value: {
      color: "augmentationPanel.text",
      fontFamily: "augmentationPanel",
      fontSize: "14px",
      fontStyle: "normal",
      fontWeight: 500,
      lineHeight: "17px"
    }
  }
})

// Global styles applied to the whole app.
const globalCss = defineGlobalStyles({
  ["[hidden]"]: { display: "none !important" }
})

export const systemConfig: SystemConfig = {
  globalCss: globalCss,
  conditions,
  preflight: false, // this maps to resetCss, we may still want this?
  theme: {
    textStyles: typographyCompatTextStyles,
    recipes: {
      button: buttonRecipe,
      container: containerRecipe,
      separator: separatorRecipe,
      heading: headingRecipe,
      input: inputRecipe,
      textarea: textareaRecipe,
      popover: popoverRecipe,
      text: textRecipe
    },
    // Partial slot recipe overrides; merged with Chakra defaults at runtime.
    slotRecipes: {
      dialog: {
        base: {
          content: { textStyle: "md" }
        }
      },
      drawer: drawerSlotRecipe,
      select: {
        variants: {
          size: {
            md: {
              trigger: { textStyle: "md" },
              content: { textStyle: "md" }
            }
          }
        }
      },
      numberInput: {
        variants: {
          size: {
            md: {
              input: { textStyle: "md" }
            }
          }
        }
      },
      avatar: {
        variants: {
          size: {
            "2xs": {
              root: {
                "--avatar-font-size": "fontSizes.2xs",
                "--avatar-size": "sizes.5"
              }
            }
          }
        }
      },
      field: fieldSlotRecipe
    } as any,
    tokens: {
      fontSizes: {
        // sm: { value: "1rem" }
      },
      fonts: {
        augmentationPanel: { value: "'Inter', sans-serif" },
        heading: {
          value: `-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen","Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",sans-serif`
        },
        body: {
          value: `-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen","Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue",sans-serif`
        }
      },
      // Increasing Chakra's zIndices by our base fullscreen zIndex value (of 1000009999)
      // so that Chakra components will actually layer on top of ours.
      zIndex: {
        hide: { value: -1 },
        auto: { value: "auto" },
        base: { value: 1000009999 },
        docked: { value: 1000010009 }, // base + 10
        dropdown: { value: 1000010999 }, // base + 1000
        sticky: { value: 1000011099 }, // base + 1100
        banner: { value: 1000011199 }, // base + 1200
        overlay: { value: 1000011299 }, // base + 1300
        modal: { value: 1000011399 }, // base + 1400
        popover: { value: 1000011499 }, // base + 1500
        skipLink: { value: 1000011599 }, // base + 1600
        toast: { value: 1000011699 }, // base + 1700
        tooltip: { value: 1000011799 } // base + 1800
      },
      radii: {
        augmentationPanel: { value: "15px" },
        // Inputs and options menu rows, which sit inside a 15px container.
        augmentationPanelField: { value: "10px" },
        card: { value: "20px" },
        button: { value: "50px" }
      },
      shadows: {
        augmentationPanel: {
          value:
            "0 0 0 1px rgba(0, 0, 0, 0.05), 0 3px 30px rgba(0, 0, 0, 0.15), inset 0 0 0 1px rgba(255, 255, 255, 0.2)"
        },
        // Keyboard focus on a panel field.
        augmentationPanelFocusRing: {
          value: "0 0 0 1px rgba(0, 0, 0, 0.25)"
        },
        // The brief emphasis a panel row takes after a hash navigation.
        augmentationPanelSectionFocus: {
          value: "inset 0 0 0 1px rgba(0, 0, 0, 0.2)"
        },
        card: { value: "0 2px 8px 0 rgba(0, 0, 0, 0.15)" },
        cardHover: { value: "0 2px 8px 0 rgba(0, 0, 0, 0.25)" }
      },
      sizes: {
        // Figma Palette width for shared augmentation panels.
        augmentationPanel: { value: "300px" },
        // For destinations that ask for room — today the legacy settings forms
        // embedded in a panel. Matched to the small legacy Card, since that's
        // the width those forms were laid out for.
        augmentationPanelWide: { value: "400px" },
        // Panel rows: list items, fields, and menu rows that list things.
        augmentationPanelRow: { value: "33px" },
        // The same row where there's no hover, i.e. a native touch target.
        augmentationPanelRowTouch: { value: "44px" },
        // Options menu rows, which act on a thing rather than listing things.
        augmentationPanelOptionsRow: { value: "30px" },
        // Leading icon, avatar, or image in a row — all share one slot so
        // every label lands on the same left edge.
        augmentationPanelIcon: { value: "16px" }
      },
      spacing: {
        augmentationPanel: { value: "15px" },
        // Horizontal inset inside a panel row, field, or menu row.
        augmentationPanelGutter: { value: "8px" }
      },
      colors: {
        // Augmentation panels sit on a translucent surface, so everything in
        // them is stated as a black alpha over whatever shows through. One
        // ramp, walked as an element goes from resting to hovered to pressed.
        augmentationPanel: {
          fill: {
            subtle: { value: "rgba(0, 0, 0, 0.05)" },
            muted: { value: "rgba(0, 0, 0, 0.1)" },
            strong: { value: "rgba(0, 0, 0, 0.15)" },
            bold: { value: "rgba(0, 0, 0, 0.25)" }
          },
          // Figma specifies pure black, which Chakra's `black` isn't (#09090b).
          text: { value: "#000" },
          // Hairline between panel sections and around a drop target.
          border: { value: "rgba(0, 0, 0, 0.1)" },
          // The panel's own surface. Translucent where the panel sits beside
          // the page and the page is the context you're working against. Where
          // the panel covers the page instead — a phone — that context is gone
          // and the translucency is just whatever happens to be behind the
          // text, so the surface closes up to a hint of what it's over.
          surface: { value: "rgba(244, 247, 250, 0.8)" },
          surfaceOverlay: { value: "rgba(244, 247, 250, 0.96)" },
          // Floating menu surfaces. The solid variant is for menus that open
          // over the panel's own list, where ghosting reads as noise.
          menuSurface: { value: "rgba(244, 247, 250, 0.9)" },
          menuSurfaceSolid: { value: "rgba(244, 247, 250, 0.98)" },
          // Compact floating toolbar island — lighter than the panel surface so
          // it reads as a tool over the page rather than another panel.
          toolbarSurface: { value: "rgba(244, 247, 250, 0.5)" }
        },
        // Legacy UI colors, would be nice if we could consolidate.
        PRIMARY_GRAY: { value: "rgba(42,46,51,1)" },
        SECONDARY_GRAY: { value: "rgba(0,0,0,0.5)" },
        TERTIARY_GRAY: { value: "rgba(0,0,0,0.4)" },
        SHADOW_GRAY: { value: "rgba(0,0,0,0.15)" },
        BORDER_GRAY: { value: "rgba(0,0,0,0.1)" },
        OVERLAY_GRAY: { value: "rgba(0,0,0,0.05)" },
        PRIMARY_WHITE: { value: "rgba(255,255,255,1)" },
        SECONDARY_WHITE: { value: "rgba(255,255,255,0.8)" },
        TERTIARY_WHITE: { value: "rgba(255,255,255,0.6)" },
        BORDER_WHITE: { value: "rgba(255,255,255,0.3)" },
        OVERLAY_WHITE: { value: "rgba(255,255,255,0.1)" },
        PRIMARY_WHITE_BACKGROUND: { value: "rgba(255,255,255,1)" },
        SECONDARY_WHITE_BACKGROUND: { value: "rgba(245,245,245,1)" },
        TERTIARY_WHITE_BACKGROUND: { value: "rgba(234,234,234,1)" },
        GHOST_ICE_BACKGROUND: { value: "rgba(249,251,253,1)" },
        FOCUS_BLUE: { value: "rgba(106,159,203,1)" },
        EDITED_ORANGE: { value: "rgba(206,150,101,1)" },
        ALERT_YELLOW: { value: "rgba(255,252,230,1)" },
        WARNING_ORANGE: { value: "rgba(255,133,27,1)" },
        DANGEROUS_RED: { value: "rgba(255,0,0,1)" },
        SUCCESS_GREEN: { value: "rgba(34,139,34,1)" },
        // -- end legacy UI colors.
        relativeTime0: {
          50: { value: "#eefbf1" },
          100: { value: "#d6f4de" },
          200: { value: "#a8e8b8" },
          300: { value: "#7edd96" },
          400: { value: "#56d375" },
          500: { value: "#34c759" },
          600: { value: "#2ba64a" },
          700: { value: "#23883d" },
          800: { value: "#1c6d31" },
          900: { value: "#165526" },
          950: { value: "#165526" }
        },
        relativeTime1: {
          50: { value: "#ffffeb" },
          100: { value: "#ffffcd" },
          200: { value: "#ffff94" },
          300: { value: "#ffff5f" },
          400: { value: "#ffff2e" },
          500: { value: "#ffff00" },
          600: { value: "#d5d500" },
          700: { value: "#afaf00" },
          800: { value: "#8c8c00" },
          900: { value: "#6d6d00" },
          950: { value: "#6d6d00" }
        },
        relativeTime2: {
          50: { value: "#fffdeb" },
          100: { value: "#fffbcd" },
          200: { value: "#fff694" },
          300: { value: "#fff25f" },
          400: { value: "#ffee2e" },
          500: { value: "#ffea00" },
          600: { value: "#d5c300" },
          700: { value: "#afa000" },
          800: { value: "#8c8000" },
          900: { value: "#6d6400" },
          950: { value: "#6d6400" }
        },
        relativeTime3: {
          50: { value: "#fffceb" },
          100: { value: "#fff7cd" },
          200: { value: "#ffed94" },
          300: { value: "#ffe55f" },
          400: { value: "#ffdd2e" },
          500: { value: "#ffd500" },
          600: { value: "#d5b200" },
          700: { value: "#af9200" },
          800: { value: "#8c7500" },
          900: { value: "#6d5b00" },
          950: { value: "#6d5b00" }
        },
        relativeTime4: {
          50: { value: "#fffaeb" },
          100: { value: "#fff2cd" },
          200: { value: "#ffe494" },
          300: { value: "#ffd75f" },
          400: { value: "#ffca2e" },
          500: { value: "#ffbf00" },
          600: { value: "#d5a000" },
          700: { value: "#af8300" },
          800: { value: "#8c6900" },
          900: { value: "#6d5200" },
          950: { value: "#6d5200" }
        },
        relativeTime5: {
          50: { value: "#fff8eb" },
          100: { value: "#ffeecd" },
          200: { value: "#ffdb94" },
          300: { value: "#ffca5f" },
          400: { value: "#ffb92e" },
          500: { value: "#ffaa00" },
          600: { value: "#d58e00" },
          700: { value: "#af7400" },
          800: { value: "#8c5d00" },
          900: { value: "#6d4900" },
          950: { value: "#6d4900" }
        },
        relativeTime6: {
          50: { value: "#fff6eb" },
          100: { value: "#ffeacd" },
          200: { value: "#ffd394" },
          300: { value: "#ffbd5f" },
          400: { value: "#ffa82e" },
          500: { value: "#ff9500" },
          600: { value: "#d57c00" },
          700: { value: "#af6600" },
          800: { value: "#8c5200" },
          900: { value: "#6d4000" },
          950: { value: "#6d4000" }
        },
        relativeTime7: {
          50: { value: "#fff5eb" },
          100: { value: "#ffe6cd" },
          200: { value: "#ffca94" },
          300: { value: "#ffaf5f" },
          400: { value: "#ff972e" },
          500: { value: "#ff8000" },
          600: { value: "#d56b00" },
          700: { value: "#af5800" },
          800: { value: "#8c4600" },
          900: { value: "#6d3700" },
          950: { value: "#6d3700" }
        },
        relativeTime8: {
          50: { value: "#fff3eb" },
          100: { value: "#ffe2cd" },
          200: { value: "#ffc194" },
          300: { value: "#ffa25f" },
          400: { value: "#ff852e" },
          500: { value: "#ff6a00" },
          600: { value: "#d55900" },
          700: { value: "#af4900" },
          800: { value: "#8c3a00" },
          900: { value: "#6d2d00" },
          950: { value: "#6d2d00" }
        },
        relativeTime9: {
          50: { value: "#fff1eb" },
          100: { value: "#ffdecd" },
          200: { value: "#ffb894" },
          300: { value: "#ff945f" },
          400: { value: "#ff732e" },
          500: { value: "#ff5500" },
          600: { value: "#d54700" },
          700: { value: "#af3a00" },
          800: { value: "#8c2f00" },
          900: { value: "#6d2400" },
          950: { value: "#6d2400" }
        },
        relativeTime10: {
          50: { value: "#fff0eb" },
          100: { value: "#ffd9cd" },
          200: { value: "#ffaf94" },
          300: { value: "#ff875f" },
          400: { value: "#ff622e" },
          500: { value: "#ff4000" },
          600: { value: "#d53500" },
          700: { value: "#af2c00" },
          800: { value: "#8c2300" },
          900: { value: "#6d1b00" },
          950: { value: "#6d1b00" }
        },
        relativeTime11: {
          50: { value: "#ffeeeb" },
          100: { value: "#ffd5cd" },
          200: { value: "#ffa694" },
          300: { value: "#ff7a5f" },
          400: { value: "#ff512e" },
          500: { value: "#ff2b00" },
          600: { value: "#d52400" },
          700: { value: "#af1d00" },
          800: { value: "#8c1800" },
          900: { value: "#6d1200" },
          950: { value: "#6d1200" }
        },
        relativeTime12: {
          50: { value: "#ffeceb" },
          100: { value: "#ffd1cd" },
          200: { value: "#ff9d94" },
          300: { value: "#ff6c5f" },
          400: { value: "#ff3f2e" },
          500: { value: "#ff1500" },
          600: { value: "#d51200" },
          700: { value: "#af0e00" },
          800: { value: "#8c0c00" },
          900: { value: "#6d0900" },
          950: { value: "#6d0900" }
        }
      }
    },
    semanticTokens: {
      colors: {
        primary: {
          value: {
            base: "{colors.PRIMARY_GRAY}",
            _dark: "white"
          }
        },
        // Chakra v2 "secondary", which we may wish to return to, or, update other semantic tokens
        // to rely more on more standard color tokens like "gray.200" etc.
        // secondary: {
        //   value: {
        //     base: "{colors.SECONDARY_GRAY}",
        //     _dark: "{colors.whiteAlpha.500}"
        //   }
        // },
        secondary: {
          value: {
            base: "{colors.gray.200}",
            _dark: "{colors.gray.700}"
          }
        },
        tertiary: {
          value: {
            base: "{colors.TERTIARY_GRAY}",
            _dark: "{colors.whiteAlpha.400}"
          }
        },
        accent: {
          value: {
            base: "{colors.brand.600}",
            _dark: "{colors.brand.600}"
          }
        },
        background: {
          value: {
            base: "{colors.gray.50}",
            _dark: "{colors.gray.900}"
          }
        },
        backgroundSecondary: {
          value: {
            base: "{colors.GHOST_ICE_BACKGROUND}",
            _dark: "{colors.blackAlpha.500}"
          }
        },
        // From web-new, may want to keep these:
        text: {
          value: {
            base: "{colors.gray.900}",
            _dark: "{colors.gray.50}"
          }
        },
        textSecondary: {
          value: {
            base: "{colors.blackAlpha.700}",
            _dark: "{colors.whiteAlpha.700}"
          }
        },
        // text that is always on a white bg
        contentText: {
          value: {
            base: "{colors.blackAlpha.900}",
            _dark: "white"
          }
        },
        contentTextSecondary: {
          value: {
            base: "{colors.blackAlpha.500}",
            _dark: "{colors.blackAlpha.500}"
          }
        },
        overlayText: {
          value: {
            base: "{colors.gray.50}",
            _dark: "{colors.gray.30}"
          }
        },
        // background for content (for readable text)
        contentBackground: {
          value: {
            base: "rgba(255,255,255,1)",
            _dark: "rgba(33, 29, 39, 1)"
          }
        },
        contentBackgroundSecondary: {
          value: {
            base: "{colors.gray.100}",
            _dark: "{colors.gray.700}"
          }
        },
        // NOTE: this border token overrides all Chakra border nested tokens (i.e. "boder.sublte" etc.).
        // You'd have define a border[nestedToken] semantic token to non-destructively add other options.
        // border: {
        //   value: {
        //     base: "{colors.blackAlpha.400}",
        //     _dark: "{colors.whiteAlpha.600}"
        //   }
        // },
        islandBackground: {
          value: {
            base: "{colors.gray.900}",
            _dark: "{colors.gray.900}"
          }
        },
        islandText: {
          value: {
            base: "{colors.gray.50}",
            _dark: "{colors.gray.50}"
          }
        },
        surface: {
          value: {
            base: "rgba(255, 255, 255, 0.88)",
            _dark: "rgba(33, 29, 39, 0.77)"
          }
        },
        surfaceThin: {
          value: {
            base: "rgba(255, 255, 255, 0.66)",
            _dark: "rgba(33, 29, 39, 0.4)"
          }
        }
      }
    }
  }
}

systemConfig.theme!.semanticTokens!.colors = {
  ...systemConfig.theme!.semanticTokens!.colors,
  ...getColorPaletteSemanticTokens("relativeTime0"),
  ...getColorPaletteSemanticTokens("relativeTime1"),
  ...getColorPaletteSemanticTokens("relativeTime2"),
  ...getColorPaletteSemanticTokens("relativeTime3"),
  ...getColorPaletteSemanticTokens("relativeTime4"),
  ...getColorPaletteSemanticTokens("relativeTime5"),
  ...getColorPaletteSemanticTokens("relativeTime6"),
  ...getColorPaletteSemanticTokens("relativeTime7"),
  ...getColorPaletteSemanticTokens("relativeTime8"),
  ...getColorPaletteSemanticTokens("relativeTime9"),
  ...getColorPaletteSemanticTokens("relativeTime10"),
  ...getColorPaletteSemanticTokens("relativeTime11"),
  ...getColorPaletteSemanticTokens("relativeTime12")
}

export const system = createSystem(defaultConfig, systemConfig)
